import type { DetectedLayer, RawFeature } from "./detectLayers.ts";
import { defaultValueFor, importColumnsFor } from "./importSchema.ts";
import {
  LINK_TARGET_SECTIONS,
  NODE_TARGET_SECTIONS,
  type ImportTargetSection,
} from "./importTargets.ts";

/** column (from `importColumnsFor`) -> source GeoJSON property name; "" or absent means "use `defaultValueFor` instead". */
export type FieldMapping = Record<string, string>;

function cloneSection(raw: ReadonlyMap<string, string[][]>, section: string): string[][] {
  return (raw.get(section) ?? []).map((row) => [...row]);
}

function propertyValue(feature: RawFeature, propertyName: string): string {
  const value = feature.properties?.[propertyName];
  return value === undefined || value === null ? "" : String(value);
}

/** Drops GeoJSON's closing duplicate point (first === last) — SWMM's [POLYGONS] rows don't repeat it. */
function ringPoints(coordinates: unknown): [number, number][] {
  const ring = ((coordinates as number[][][]) ?? [])[0] ?? [];
  const points = ring.map(([x, y]) => [x, y] as [number, number]);
  if (points.length > 1) {
    const [firstX, firstY] = points[0];
    const [lastX, lastY] = points[points.length - 1];
    if (firstX === lastX && firstY === lastY) points.pop();
  }
  return points;
}

interface ImportResult {
  raw: ReadonlyMap<string, string[][]>;
  createdCount: number;
  skippedCount: number;
  placeholderNames: string[];
}

/**
 * A feature with an empty/missing name, or one that collides with a name
 * already in the project (including one created earlier in this same
 * import), is skipped rather than silently overwriting an existing element.
 *
 * Also fills in whichever companion section a fresh element of this kind
 * always needs to actually render/simulate — [COORDINATES] for a node,
 * [POLYGONS]+[SUBAREAS]+[INFILTRATION] for a subcatchment,
 * [XSECTIONS]+[VERTICES] for a link — since the mapping UI only ever offers
 * `section`'s own primary columns, not these.
 */
export function importLayer(
  raw: ReadonlyMap<string, string[][]>,
  layer: DetectedLayer,
  section: ImportTargetSection,
  nameProperty: string,
  fieldMapping: FieldMapping,
  /** Identity by default so every pre-existing caller/test keeps behaving exactly as before. */
  reprojectPoint: (x: number, y: number) => [number, number] = (x, y) => [x, y]
): ImportResult {
  const columns = importColumnsFor(section);
  const existingNames = new Set<string>();
  for (const rows of raw.values()) for (const row of rows) if (row[0]) existingNames.add(row[0]);

  const sectionRows = cloneSection(raw, section);
  const coordinateRows = cloneSection(raw, "COORDINATES");
  const polygonRows = cloneSection(raw, "POLYGONS");
  const vertexRows = cloneSection(raw, "VERTICES");
  const xsectionRows = cloneSection(raw, "XSECTIONS");
  const subareaRows = cloneSection(raw, "SUBAREAS");
  const infiltrationRows = cloneSection(raw, "INFILTRATION");

  let createdCount = 0;
  let skippedCount = 0;
  const placeholderNames: string[] = [];

  for (const feature of layer.featureCollection.features) {
    const name = propertyValue(feature, nameProperty).trim();
    if (!name || existingNames.has(name)) {
      skippedCount++;
      continue;
    }
    existingNames.add(name);

    let hasPlaceholder = false;
    const values = columns.map((column) => {
      const property = fieldMapping[column];
      const mapped = property ? propertyValue(feature, property) : "";
      if (mapped === "") hasPlaceholder = true;
      return mapped !== "" ? mapped : defaultValueFor(column);
    });
    sectionRows.push([name, ...values]);
    if (hasPlaceholder) placeholderNames.push(name);

    const coordinates = feature.geometry?.coordinates;
    if (NODE_TARGET_SECTIONS.has(section) && Array.isArray(coordinates)) {
      const [x, y] = coordinates as [number, number];
      const [rx, ry] = reprojectPoint(x, y);
      coordinateRows.push([name, String(rx), String(ry)]);
    } else if (section === "SUBCATCHMENTS" && coordinates) {
      for (const [x, y] of ringPoints(coordinates)) {
        const [rx, ry] = reprojectPoint(x, y);
        polygonRows.push([name, String(rx), String(ry)]);
      }
      subareaRows.push([name, "0", "0", "0", "0", "0", "OUTLET", "0"]);
      infiltrationRows.push([name, "0", "0", "0", "0", "0"]);
    } else if (LINK_TARGET_SECTIONS.has(section) && Array.isArray(coordinates)) {
      const line = coordinates as [number, number][];
      for (const [x, y] of line.slice(1, -1)) {
        const [rx, ry] = reprojectPoint(x, y);
        vertexRows.push([name, String(rx), String(ry)]);
      }
      xsectionRows.push([name, "CIRCULAR", "1", "0", "0", "0", "1"]);
    }

    createdCount++;
  }

  const next = new Map(raw);
  next.set(section, sectionRows);
  if (NODE_TARGET_SECTIONS.has(section)) next.set("COORDINATES", coordinateRows);
  if (section === "SUBCATCHMENTS") {
    next.set("POLYGONS", polygonRows);
    next.set("SUBAREAS", subareaRows);
    next.set("INFILTRATION", infiltrationRows);
  }
  if (LINK_TARGET_SECTIONS.has(section)) {
    next.set("VERTICES", vertexRows);
    next.set("XSECTIONS", xsectionRows);
  }

  return { raw: next, createdCount, skippedCount, placeholderNames };
}
